import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { fetchAccountOrders, openBillingPortal, gbp } from "../lib/api";

const STATUS_COLORS = {
  paid: "bg-green-100 text-green-800 border-green-300",
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  initiated: "bg-yellow-100 text-yellow-800 border-yellow-300",
  expired: "bg-red-100 text-red-800 border-red-300",
  abandoned: "bg-gray-100 text-gray-600 border-gray-300",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

export default function Account() {
  const { customer, logoutCustomer } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (customer === false) {
      navigate("/login", { replace: true });
    }
  }, [customer, navigate]);

  useEffect(() => {
    if (!customer || customer === false) return;
    (async () => {
      try {
        const res = await fetchAccountOrders();
        setOrders(res.orders || []);
      } catch {
        toast.error("Could not load your orders.");
        setOrders([]);
      }
    })();
  }, [customer]);

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const res = await openBillingPortal();
      window.location.href = res.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open subscription portal.");
      setPortalLoading(false);
    }
  };

  const handleLogout = async () => {
    await logoutCustomer();
    navigate("/", { replace: true });
  };

  if (!customer || customer === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream text-forest text-sm tracking-widest uppercase">
        Loading account…
      </div>
    );
  }

  const paidOrders = (orders || []).filter((o) => o.payment_status === "paid");
  const subOrders = paidOrders.filter((o) => o.has_subscription);

  return (
    <div className="min-h-screen bg-cream text-forest grain">
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between border-b border-forest/10">
        <Link to="/" className="tracking-widest text-sm font-semibold uppercase" data-testid="account-home-link">
          Prestige Wellness Co
        </Link>
        <div className="flex items-center gap-4">
          {customer.picture && (
            <img
              src={customer.picture}
              alt=""
              className="w-8 h-8 rounded-full border border-forest/20"
              referrerPolicy="no-referrer"
            />
          )}
          <span className="hidden sm:inline text-sm">{customer.name || customer.email}</span>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="text-xs uppercase tracking-widest text-forest/60 hover:text-forest"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-10 py-14">
        <p className="text-xs uppercase tracking-[0.28em] text-forest/60 mb-3">Members</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-3" data-testid="account-heading">
          Your Wellness, on autopilot.
        </h1>
        <p className="text-forest/70 mb-10 max-w-xl">
          Track your orders and manage your monthly subscription plans in one place.
        </p>

        {/* subscriptions card */}
        <section className="rounded-2xl border border-forest/15 bg-white/70 backdrop-blur p-6 sm:p-8 mb-10">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <h2 className="font-serif text-2xl mb-1">Subscriptions</h2>
              <p className="text-sm text-forest/60 max-w-md">
                You have <strong data-testid="active-sub-count">{subOrders.length}</strong> active monthly plan{subOrders.length === 1 ? "" : "s"}.
                Pause, skip a month, cancel, or update your card in your Stripe portal.
              </p>
            </div>
            <button
              onClick={handlePortal}
              disabled={portalLoading}
              data-testid="open-billing-portal-btn"
              className="px-6 py-3 rounded-full bg-forest text-cream text-xs uppercase tracking-widest hover:bg-forest/90 disabled:opacity-60"
            >
              {portalLoading ? "Opening…" : "Manage subscriptions →"}
            </button>
          </div>
        </section>

        {/* orders */}
        <section>
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-serif text-2xl">Order history</h2>
            <span className="text-xs uppercase tracking-widest text-forest/50" data-testid="order-count">
              {(orders || []).length} orders
            </span>
          </div>

          {orders === null && (
            <div className="text-sm text-forest/50 py-10">Loading orders…</div>
          )}

          {orders !== null && orders.length === 0 && (
            <div className="text-center py-14 border border-dashed border-forest/20 rounded-xl">
              <p className="text-forest/60 mb-4">No orders yet.</p>
              <Link
                to="/"
                className="inline-block px-6 py-3 rounded-full bg-forest text-cream text-xs uppercase tracking-widest"
              >
                Shop the range
              </Link>
            </div>
          )}

          {orders !== null && orders.length > 0 && (
            <div className="space-y-3" data-testid="orders-list">
              {orders.map((o) => (
                <div
                  key={o.order_id || o.session_id}
                  className="flex items-start justify-between gap-6 p-5 rounded-xl border border-forest/15 bg-white/60"
                  data-testid={`order-${o.order_id}`}
                >
                  <div>
                    <div className="text-xs uppercase tracking-widest text-forest/50 mb-1">
                      {formatDate(o.created_at)} · #{(o.order_id || "").slice(0, 8)}
                    </div>
                    <ul className="text-sm text-forest">
                      {(o.items || []).map((it, i) => (
                        <li key={i}>
                          {it.quantity} × {it.name}
                          {it.plan === "subscribe" && (
                            <span className="ml-2 text-[10px] uppercase tracking-widest text-forest/50">Monthly</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-serif text-lg">{gbp(o.amount || 0)}</div>
                    <span
                      className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${
                        STATUS_COLORS[o.payment_status] || STATUS_COLORS.pending
                      }`}
                    >
                      {o.payment_status || "pending"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
